import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const DocumentReviewPanel = ({ 
  application = null, 
  isOpen = false, 
  onClose = () => {}, 
  onApprove = () => {},
  onReject = () => {},
  onAddNote = () => {}
}) => {
  const [activeDocument, setActiveDocument] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [documentNotes, setDocumentNotes] = useState({});
  const [signedUrls, setSignedUrls] = useState({});
  const [loadingUrls, setLoadingUrls] = useState(false);
  const [urlErrors, setUrlErrors] = useState({});
  const [documentError, setDocumentError] = useState('');
  const [loadingDocument, setLoadingDocument] = useState(false);

  const documentTypes = {
    incorporation: {
      label: 'Incorporation Certificate',
      icon: 'FileText',
      description: 'Certificate of incorporation or business registration',
      dbKey: 'incorporation_certificate'
    },
    travel_authority: {
      label: 'Travel Authority License (ATOL/ABTA)',
      icon: 'Shield',
      description: 'ATOL, ABTA or equivalent travel authority certification',
      dbKey: 'travel_license'
    },
    insurance: {
      label: 'Professional Insurance',
      icon: 'Umbrella',
      description: 'Professional indemnity and public liability insurance',
      dbKey: 'professional_insurance'
    },
    terms: {
      label: 'Terms & Conditions',
      icon: 'ScrollText',
      description: 'Business terms and conditions document',
      dbKey: 'terms_conditions'
    },
    privacy: {
      label: 'Privacy Policy',
      icon: 'Lock',
      description: 'Data protection and privacy policy document',
      dbKey: 'privacy_policy'
    }
  };

  // PRE-GENERATE SIGNED URLs when panel opens or application changes
  useEffect(() => {
    const prefetchSignedUrls = async () => {
      if (!application?.documents || !isOpen) {
        setSignedUrls({});
        setUrlErrors({});
        return;
      }

      setLoadingUrls(true);
      const urls = {};
      const errors = {};

      // Loop through all document types and generate signed URLs for available documents
      for (const [docType, config] of Object.entries(documentTypes)) {
        const documentData = application?.documents?.[docType];
        
        if (documentData?.file_path) {
          try {
            console.log(`Pre-generating signed URL for ${docType}:`, documentData?.file_path);
            
            const { data, error } = await supabase?.storage
              ?.from('agent-documents')
              ?.createSignedUrl(documentData?.file_path, 600); // 10 minutes expiry

            if (error) {
              console.error(`Error generating signed URL for ${docType}:`, error);
              errors[docType] = error?.message || 'Failed to generate document link';
            } else if (data?.signedUrl) {
              urls[docType] = data?.signedUrl;
              console.log(`✅ Signed URL ready for ${docType}`);
            } else {
              errors[docType] = 'No signed URL returned from Supabase';
            }
          } catch (err) {
            console.error(`Exception generating signed URL for ${docType}:`, err);
            errors[docType] = err?.message || 'Unexpected error generating document link';
          }
        }
      }

      setSignedUrls(urls);
      setUrlErrors(errors);
      setLoadingUrls(false);
    };

    prefetchSignedUrls();
  }, [application?.id, isOpen]);

  if (!isOpen || !application) return null;

  const handleDocumentClick = (docType) => {
    setActiveDocument(docType);
    setDocumentError('');
  };

  // Generate signed URL for document access
  const generateSignedUrl = async (filePath) => {
    try {
      console.log({ bucket: 'agent-documents', path: filePath });
      
      const { data, error } = await supabase?.storage?.from('agent-documents')?.createSignedUrl(filePath, 60); // 60 seconds expiry

      if (error) {
        console.error('Signed URL error:', error);
        throw error;
      }

      if (!data?.signedUrl) {
        throw new Error('No signed URL returned from Supabase');
      }

      console.log({ signedUrl: data?.signedUrl });
      return data?.signedUrl;
    } catch (err) {
      console.error('Error generating signed URL:', err);
      setDocumentError(err?.message || 'Failed to generate document URL');
      throw err;
    }
  };

  // Handle view document in new tab
  const handleViewDocument = async (docType) => {
    try {
      setLoadingDocument(true);
      setDocumentError('');
      
      const documentData = application?.documents?.[docType];
      if (!documentData?.file_path) {
        throw new Error('Document path not found');
      }

      const signedUrl = await generateSignedUrl(documentData?.file_path);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
      
    } catch (err) {
      console.error('Error viewing document:', err);
      setDocumentError(err?.message || 'Failed to open document');
    } finally {
      setLoadingDocument(false);
    }
  };

  // Handle download document
  const handleDownloadDocument = async (docType) => {
    try {
      setLoadingDocument(true);
      setDocumentError('');
      
      const documentData = application?.documents?.[docType];
      if (!documentData?.file_path) {
        throw new Error('Document path not found');
      }

      const signedUrl = await generateSignedUrl(documentData?.file_path);
      
      // Create temporary anchor element for download
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = documentData?.file_path?.split('/')?.pop() || 'document';
      link.target = '_blank';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      
    } catch (err) {
      console.error('Error downloading document:', err);
      setDocumentError(err?.message || 'Failed to download document');
    } finally {
      setLoadingDocument(false);
    }
  };

  // Handle open in new tab
  const handleOpenInTab = async (docType) => {
    try {
      setLoadingDocument(true);
      setDocumentError('');
      
      const documentData = application?.documents?.[docType];
      if (!documentData?.file_path) {
        throw new Error('Document path not found');
      }

      const signedUrl = await generateSignedUrl(documentData?.file_path);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
      
    } catch (err) {
      console.error('Error opening document:', err);
      setDocumentError(err?.message || 'Failed to open document');
    } finally {
      setLoadingDocument(false);
    }
  };

  const handleAddDocumentNote = (docType, note) => {
    setDocumentNotes(prev => ({
      ...prev,
      [docType]: note
    }));
    onAddNote(application?.id, docType, note);
  };

  const getDocumentStatus = (docType) => {
    return application?.documents?.[docType] ? 'submitted' : 'missing';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'submitted':
        return { icon: 'CheckCircle', color: 'text-success' };
      case 'missing':
        return { icon: 'AlertCircle', color: 'text-warning' };
      default:
        return { icon: 'Circle', color: 'text-muted-foreground' };
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border shadow-elevated w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Document Review</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {application?.businessName} - Application #{application?.id}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Document List */}
          <div className="w-1/3 border-r border-border p-6 overflow-y-auto">
            <h3 className="font-medium text-foreground mb-4">Required Documents</h3>
            
            <div className="space-y-3">
              {Object.entries(documentTypes)?.map(([type, config]) => {
                const status = getDocumentStatus(type);
                const statusConfig = getStatusIcon(status);
                
                return (
                  <div
                    key={type}
                    onClick={() => handleDocumentClick(type)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      activeDocument === type
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <Icon name={config?.icon} size={20} className="text-muted-foreground mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="text-sm font-medium text-foreground truncate">
                            {config?.label}
                          </h4>
                          <Icon 
                            name={statusConfig?.icon} 
                            size={16} 
                            className={statusConfig?.color}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {config?.description}
                        </p>
                        {status === 'submitted' && (
                          <div className="mt-2 text-xs text-success font-medium">
                            Submitted
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Application Summary */}
            <div className="mt-6 p-4 bg-muted/30 rounded-lg">
              <h4 className="font-medium text-foreground mb-3">Application Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Business Type:</span>
                  <span className="text-foreground">{application?.businessType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Contact:</span>
                  <span className="text-foreground">{application?.contactEmail}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Submitted:</span>
                  <span className="text-foreground">
                    {new Date(application.submissionDate)?.toLocaleDateString('en-GB')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Documents:</span>
                  <span className="text-foreground">
                    {Object.values(application?.documents)?.filter(Boolean)?.length}/5
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Document Viewer */}
          <div className="flex-1 flex flex-col">
            {activeDocument ? (
              <>
                {/* Document Header */}
                <div className="p-6 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-foreground">
                        {documentTypes?.[activeDocument]?.label}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {documentTypes?.[activeDocument]?.description}
                      </p>
                    </div>
                    
                    {getDocumentStatus(activeDocument) === 'submitted' && (
                      <div className="flex items-center space-x-2">
                        {/* STANDARD LINK: Download */}
                        {loadingUrls ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled
                            iconName="Loader2"
                            iconSize={14}
                          >
                            Generating link...
                          </Button>
                        ) : urlErrors?.[activeDocument] ? (
                          <div className="text-xs text-destructive">
                            Unable to generate document link
                          </div>
                        ) : signedUrls?.[activeDocument] ? (
                          <>
                            <a
                              href={signedUrls?.[activeDocument]}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium transition-colors rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                            >
                              <Icon name="Download" size={14} />
                              Download
                            </a>
                            <a
                              href={signedUrls?.[activeDocument]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium transition-colors rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                            >
                              <Icon name="ExternalLink" size={14} />
                              Open
                            </a>
                          </>
                        ) : null}
                      </div>
                    )}
                  </div>

                  {/* Error Display */}
                  {urlErrors?.[activeDocument] && (
                    <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <Icon name="AlertTriangle" size={16} className="text-destructive mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-destructive font-medium">Error accessing document</p>
                          <p className="text-xs text-destructive/80 mt-1">{urlErrors?.[activeDocument]}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Document Content */}
                <div className="flex-1 p-6">
                  {getDocumentStatus(activeDocument) === 'submitted' ? (
                    <div className="h-full bg-muted/20 rounded-lg border-2 border-dashed border-border flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground" />
                        <h4 className="font-medium text-foreground mb-2">Document Preview</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          {documentTypes?.[activeDocument]?.label} is available for review
                        </p>
                        
                        {/* STANDARD LINK: View Document */}
                        {loadingUrls ? (
                          <Button 
                            variant="outline" 
                            disabled
                            iconName="Loader2" 
                            iconSize={16}
                          >
                            Generating link...
                          </Button>
                        ) : urlErrors?.[activeDocument] ? (
                          <div className="text-sm text-destructive">
                            Unable to generate document link
                          </div>
                        ) : signedUrls?.[activeDocument] ? (
                          <a
                            href={signedUrls?.[activeDocument]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                          >
                            <Icon name="Eye" size={16} />
                            View document
                          </a>
                        ) : (
                          <div className="text-sm text-muted-foreground">
                            Document link not available
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="h-full bg-warning/10 rounded-lg border-2 border-dashed border-warning/30 flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="AlertTriangle" size={48} className="mx-auto mb-4 text-warning" />
                        <h4 className="font-medium text-foreground mb-2">Document Missing</h4>
                        <p className="text-sm text-muted-foreground">
                          This required document has not been submitted yet
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Document Notes */}
                <div className="p-6 border-t border-border">
                  <h4 className="font-medium text-foreground mb-3">Review Notes</h4>
                  <Input
                    type="text"
                    placeholder={`Add notes for ${documentTypes?.[activeDocument]?.label}...`}
                    value={documentNotes?.[activeDocument] || ''}
                    onChange={(e) => setDocumentNotes(prev => ({
                      ...prev,
                      [activeDocument]: e?.target?.value
                    }))}
                    className="mb-3"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddDocumentNote(activeDocument, documentNotes?.[activeDocument])}
                    iconName="Plus"
                    iconSize={14}
                  >
                    Add Note
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Icon name="FileSearch" size={64} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-medium text-foreground mb-2">Select a Document</h3>
                  <p className="text-muted-foreground">
                    Choose a document from the list to review its contents and add notes
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/20">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Add overall review notes..."
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e?.target?.value)}
              className="max-w-md"
            />
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              variant="destructive"
              onClick={() => onReject(application?.id, reviewNotes)}
              iconName="X"
              iconSize={16}
            >
              Reject Application
            </Button>
            <Button
              variant="success"
              onClick={() => onApprove(application?.id, reviewNotes)}
              iconName="Check"
              iconSize={16}
            >
              Approve Application
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentReviewPanel;